import forge from 'apis/forge';
import { IconType } from 'components/Icon';
import {
	distanceBetweenPoints,
	Point,
	Rect,
	rectContainsRect,
	rectsOverlap,
} from 'dragonmouse-shared/geometry';
import {
	Chat,
	ChatEntryRestriction,
	Device,
	Frame,
	FRAME_GAP,
	FrameStatus,
	FrameType,
	SelectionInfo,
} from 'dragonmouse-shared/interfaces';
import { transformCanvasToShowSceneBoundingBox } from 'redux/mode/actions';
import { getVisibleCanvasCenterInSceneCoordinates } from 'redux/mode/util';
import store from 'redux/store';
import { frameImageUrl, frameSiteExportUrl, frameSiteUrl } from 'utils';
import { v4 as uuid } from 'uuid';
import { isForgeOrigin, sendToDevigner } from '../../devigner';
import { OptimisticWrite, selectChats, selectFrames } from './reducers';

// EVERYTHING IN THIS FILE IS IN SCENE COORDINATES
// (unless otherwise noted)

// Checks if an optimistic write matches any of the provided ids
// and therefore is resolved (is no longer needed, can be removed).
export const optimisticWriteIsResolvedByIds = <T>(
	write: OptimisticWrite<T>,
	writeIds: string[]
) => {
	// The obvious case is when the write id just in the list of ids.
	// Note that a delete can match this way sometimes too, for example if a serve
	// call fails, the writeId may still be around to use during resolution.
	if (writeIds.includes(write.id)) {
		return true;
	}

	// A trickier case is when the write operation is a delete and the resolution
	// comes from ElectricSQL syncing down the operation. When something is
	// deleted, the message from ElectricSQL does not include a writeId (or any
	// other data from the deleted item, except its id). An id prefixed with
	// "delete:" is used to identify these ids. So we want to match the id of the
	// item being deleted by the write. For example, a deleted frame will be
	// identified by: "delete:<FRAME_ID>"
	if (write.operation === 'delete' && writeIds.includes(`delete:${write.value.id}`)) {
		return true;
	}
	return false;
};

//-----------------------------------------------------------------------------
// frame
//-----------------------------------------------------------------------------

// if a frame contains the provided rect, it is returned
// if no frames contain the rect, undefined is returned
//
// rect is in scene coordinates (this should be obvious as this is a scene util method)
//
// if multiple frames contain the provided rect, the one that's last in the frame list is returned
// (currently, frames are rendered in array order, so the topmost frame will be returned)
export const getFrameContaining = (rect: Rect): Frame | null => {
	const state = store.getState();
	const frames = selectFrames(state.db);

	// Find the last frame that contains the rect (visually on top)
	for (let i = frames.length - 1; i >= 0; i--) {
		const frame = frames[i];
		const bounds = getFrameBounds(frame);
		if (bounds && rectContainsRect(bounds, rect)) {
			return frame;
		}
	}

	return null;
};

export const getFrame = (id: string): Frame | null => {
	const state = store.getState();
	const frames = selectFrames(state.db);
	return frames.find((frame) => frame?.id === id) || null;
};

export const getFrames = (ids: string[]): Frame[] => {
	const state = store.getState();
	const frames = selectFrames(state.db);
	return frames.filter((frame) => ids.includes(frame.id));
};

export enum Direction {
	RIGHT = 'right',
	LEFT = 'left',
	UP = 'up',
	DOWN = 'down',
}

export const getAdjacentFrame = (frameId: string, direction: Direction): Frame | null => {
	const state = store.getState();
	const frames = selectFrames(state.db);
	const frame = frames.find((f) => f.id === frameId) || null;
	if (!frame) return null;

	const directionFilters = {
		[Direction.RIGHT]: (f: Frame) =>
			f.id !== frame.id &&
			f.x > frame.x + frame.width &&
			f.y + f.height > frame.y &&
			f.y < frame.y + frame.height,
		[Direction.LEFT]: (f: Frame) =>
			f.id !== frame.id &&
			f.x + f.width < frame.x &&
			f.y + f.height > frame.y &&
			f.y < frame.y + frame.height,
		[Direction.DOWN]: (f: Frame) =>
			f.id !== frame.id &&
			f.y > frame.y + frame.height &&
			f.x + f.width > frame.x &&
			f.x < frame.x + frame.width,
		[Direction.UP]: (f: Frame) =>
			f.id !== frame.id &&
			f.y + f.height < frame.y &&
			f.x + f.width > frame.x &&
			f.x < frame.x + frame.width,
	};

	const adjacentFrames = frames.filter(directionFilters[direction]);

	const comparators = {
		[Direction.RIGHT]: (f: Frame) => f.x,
		[Direction.LEFT]: (f: Frame) => -f.x,
		[Direction.DOWN]: (f: Frame) => f.y,
		[Direction.UP]: (f: Frame) => -f.y,
	};

	// return the closest frame in the specified direction
	return adjacentFrames.length > 0
		? adjacentFrames.reduce((closest, current) =>
				comparators[direction](current) < comparators[direction](closest) ? current : closest
		  )
		: null;
};

export const getFrameToTheRight = (frameId: string): Frame | null => {
	const state = store.getState();
	const frames = selectFrames(state.db);
	const frame = frames.find((f) => f.id === frameId) || null;
	if (!frame) return null;

	// find all the frames to the right of the current frame that also have some
	// overlap along the y axis
	const framesToTheRight = frames.filter(
		(f) =>
			f.id !== frame.id &&
			f.x > frame.x + frame.width &&
			f.y + f.height > frame.y &&
			f.y < frame.y + frame.height
	);

	// return the frame to the right with the lowest x value
	return framesToTheRight.reduce((leastXFrame, currentFrame) => {
		return currentFrame.x < leastXFrame.x ? currentFrame : leastXFrame;
	}, framesToTheRight[0]);
};

export const getFrameNearestScenePoint = (point: Point): Frame | undefined => {
	const state = store.getState();
	const frames = selectFrames(state.db);

	let closestFrame: Frame | undefined;
	let closestDistance = Infinity;

	frames.forEach((frame: Frame) => {
		const center = getFrameCenter(frame);
		const distance = distanceBetweenPoints(center, point);
		if (distance < closestDistance) {
			closestFrame = frame;
			closestDistance = distance;
		}
	});

	return closestFrame;
};

export const getFrameBounds = (frame: Frame): Rect => {
	return {
		x: frame.x,
		y: frame.y,
		width: frame.width,
		height: frame.height,
	};
};

export const getFrameCenter = (frame: Frame): Point => {
	return {
		x: frame.x + frame.width / 2,
		y: frame.y + frame.height / 2,
	};
};

export enum SearchDirection {
	RIGHT = 'right',
	DOWN = 'down',
}

// Given "bounds" which is a rect in scene coordinates,
//  - is bounds unoccupied? If so, return bounds.
//  - if not, search in the provided direction until we find an unoccupied spot, and return that.
//
// Currently, this routine fans out a bit as it searches. So if direction is down,
// it not only shifts down but also to the left and right a bit to find a good spot.
export const findNextFramePosition = (
	bounds: Rect,
	direction: SearchDirection = SearchDirection.DOWN
): Rect => {
	const state = store.getState();
	const frames = selectFrames(state.db);

	// gather all items in the scene into a collection of bounds we will test against
	const testBoundsCollection: Rect[] = [...frames.map((frame) => getFrameBounds(frame))];

	let current = { x: bounds.x, y: bounds.y };
	let hasOverlap = true;
	let iterations = 0;
	const MAX_ITERATIONS = 100;

	while (hasOverlap && iterations < MAX_ITERATIONS) {
		iterations++;
		hasOverlap = false;

		// Test against all bounds (frames and chats)
		for (const testBounds of testBoundsCollection) {
			if (
				rectsOverlap(
					{ x: current.x, y: current.y, width: bounds.width, height: bounds.height },
					testBounds
				)
			) {
				hasOverlap = true;
				switch (direction) {
					case SearchDirection.RIGHT:
						current.x = testBounds.x + testBounds.width + FRAME_GAP;
						break;
					case SearchDirection.DOWN:
						current.y = testBounds.y + testBounds.height + FRAME_GAP;
						break;
				}
				break;
			}
		}
	}

	return { x: current.x, y: current.y, width: bounds.width, height: bounds.height };
};

export const getRightmostFrameX = (): number | null => {
	const state = store.getState();
	const frames = selectFrames(state.db);
	if (frames.length === 0) return null;
	const rightmostFrameX = frames.reduce((max, frame) => Math.max(max, frame.x + frame.width), 0);
	return rightmostFrameX;
};

export const getFrameImage = async (frame: Frame): Promise<Blob> => {
	const url = frameImageUrl(frame, true);
	if (!url) throw new Error('No URL for screen.');
	const response = await fetch(url, {
		credentials: 'include', // This ensures cookies/auth are sent
	});
	if (!response.ok) throw new Error('Failed to fetch image');

	const reader = response.body?.getReader();
	if (!reader) throw new Error('No readable stream available');

	const chunks: Uint8Array[] = [];
	while (true) {
		const { done, value } = await reader.read();
		if (done) break;
		chunks.push(value);
	}

	const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
	const concatenated = new Uint8Array(totalLength);
	let position = 0;
	for (const chunk of chunks) {
		concatenated.set(chunk, position);
		position += chunk.length;
	}

	return new Blob([concatenated], { type: 'image/png' });
};

export const getFrameSvg = async (frame: Frame): Promise<Blob> => {
	if (!frame.siteId) {
		throw new Error('Frame has no siteId');
	}

	const timestamp = Date.now();
	const secondsSinceTimestamp = () => (Date.now() - timestamp) / 1000;

	const siteUrl = frameSiteUrl(frame, { devignSvg: true });
	if (!siteUrl) {
		throw new Error('Could not generate site URL');
	}

	console.log('** 0 **', secondsSinceTimestamp());
	const svg = await new Promise<string>((resolve, reject) => {
		// Create an invisible iframe
		console.log('** 1 **', secondsSinceTimestamp());
		const iframe = document.createElement('iframe');
		iframe.style.visibility = 'hidden';
		iframe.style.pointerEvents = 'none';
		iframe.src = siteUrl;
		iframe.style.width = frame.width.toString() + 'px';
		iframe.style.height = frame.height.toString() + 'px';

		// Set up message listener
		const messageHandler = (event: MessageEvent) => {
			console.log('** 3 **', secondsSinceTimestamp(), event.origin, event.data);
			if (!isForgeOrigin(event)) return;
			if (event.data?.type !== 'svg') return;
			if (event.data.siteId !== frame.siteId) return;

			// Clear the timeout since we got our data
			clearTimeout(timeoutId);

			// Remove iframe after we get our image
			window.removeEventListener('message', messageHandler);
			document.body.removeChild(iframe);

			// Process the SVG
			const svg = event.data.svg as string;
			if (!svg) {
				reject(new Error('No SVG data received'));
				return;
			}
			resolve(svg);
		};

		window.addEventListener('message', messageHandler);
		document.body.appendChild(iframe);
		console.log('** 2 **', secondsSinceTimestamp());

		// Set a timeout in case the iframe never responds
		const timeoutId = setTimeout(() => {
			console.log('** 4 **', secondsSinceTimestamp());
			window.removeEventListener('message', messageHandler);
			document.body.removeChild(iframe);
			reject(new Error('Timeout waiting for SVG data'));
		}, 15000); // 15 second timeout
	});

	console.log('** 5 **', secondsSinceTimestamp());
	const response = await forge.inlineSvg({
		data: svg,
		width: frame.width,
		height: frame.height,
	});
	console.log('** 6 **', secondsSinceTimestamp());
	if (!response.data) throw new Error('Failed to fetch convert SVG to inline');

	console.log('** 7 **', secondsSinceTimestamp());
	return new Blob([response.data], { type: 'image/svg+xml' });
};

export const getFrameExportHtml = async (frame: Frame): Promise<Blob> => {
	if (!frame.siteId) throw new Error('Frame has no siteId');

	const url = frameSiteExportUrl(frame);
	if (!url) throw new Error('Could not generate site URL');

	const response = await fetch(url, {
		credentials: 'include', // This ensures cookies/auth are sent
	});
	if (!response.ok) throw new Error('Failed to fetch HTML');

	const html = await response.text();
	return new Blob([html], { type: 'text/html' });
};

// frames don't have a device, they just have a width and height! so we have this procedure to try to generate the right one
// looks through the provided devices array and returns the first Device that matches the frame's width and height
export const getDeviceFromFrameId = (frameId: string, devices: Device[]): Device | null => {
	const state = store.getState();
	const frames = selectFrames(state.db);
	const frame = frames.find((frame) => frame?.id === frameId) || null;

	if (devices.length === 0) {
		return null;
	}
	for (const d of devices) {
		if (d.width === frame?.width && d.height === frame?.height) {
			return d;
		}
	}
	return null;
};

export const getFrameIconType = (frame: Frame): IconType => {
	switch (frame.metadata.type) {
		case FrameType.ADJUST:
			return IconType.ADJUST;
		case FrameType.GENERATE:
		case FrameType.GENERATE_FROM_CHAT:
			return IconType.GENERATE;
		case FrameType.IMPORT_SITE:
		case FrameType.IMPORT:
			return IconType.IMPORT;
		case FrameType.MERGE:
			return IconType.MERGE;
		case FrameType.PROTOTYPE:
			return IconType.PROTOTYPE;
	}
	return IconType.FRAME;
};

export const canRegenerateFrame = (frame: Frame): boolean => {
	if (frame.metadata.status !== FrameStatus.READY) return false;
	return (
		frame.metadata.type === FrameType.GENERATE ||
		frame.metadata.type === FrameType.GENERATE_FROM_CHAT
	);
};

export const canEnhanceFrame = (frame: Frame): boolean => {
	if (frame.metadata.status !== FrameStatus.READY) return false;
	return frame.metadata.type !== FrameType.IMPORT && frame.metadata.type !== FrameType.SYSTEM;
};

export const canExportSvg = (frames: Frame[]): boolean => {
	if (frames.every((frame) => frame.metadata.type === FrameType.IMPORT)) return false;
	return true;
};

// creates a group of count frames of the provided type, device, etc.
//
// if relativeToBounds is provided, try to place the new frames relative to these bounds
// if not, try to place the frames in the center of the screen
// (if these spots are taken, search for a sensible available spot to put them)
export const createFrames = async (
	count: number,
	device: Device,
	frameType: FrameType,
	titlePrefix: string,
	displayLabels: Record<string, string>,
	sourceFrameIds: string[],
	sourceChatId: string | null,
	relativeToBounds: Rect | null
): Promise<string[]> => {
	const state = store.getState();
	const activeSceneId = state.mode.activeSceneId;

	if (!activeSceneId) {
		throw new Error('No active scene ID found');
	}

	const totalWidth = count * device.width + (count - 1) * FRAME_GAP;
	const totalHeight = device.height;

	const sourceFrames = getFrames(sourceFrameIds);
	const hasChatSource: boolean = sourceFrames.some((frame) => frame.metadata.sourceChatId);
	const direction: SearchDirection = hasChatSource ? SearchDirection.RIGHT : SearchDirection.DOWN;

	const idealBounds: Rect = {
		x: 0,
		y: 0,
		width: totalWidth,
		height: totalHeight,
	};

	if (relativeToBounds) {
		// set x and y so that idealBounds is "direction" away from the relativeToBounds
		if (direction === SearchDirection.RIGHT) {
			idealBounds.x = relativeToBounds.x + relativeToBounds.width + FRAME_GAP;
			// the y-center of the idealBounds should be the same as the y-center of the relativeToBounds
			// relativeToBounds.y + relativeToBounds.height / 2 == idealBounds.y + totalHeight / 2
			idealBounds.y = Math.round(
				relativeToBounds.y + relativeToBounds.height / 2 - totalHeight / 2
			);
		} else {
			// the x-center of the idealBounds should be the same as the x-center of the relativeToBounds
			// relativeToBounds.x + relativeToBounds.width / 2 == idealBounds.x + totalWidth / 2
			idealBounds.x = Math.round(relativeToBounds.x + relativeToBounds.width / 2 - totalWidth / 2);
			// the top of the idealBounds should be the same as the top of the relativeToBounds
			idealBounds.y = relativeToBounds.y + relativeToBounds.height + FRAME_GAP;
		}
	} else {
		// set x and y so that the center of the idealBounds is in the center of the screen
		const center = getVisibleCanvasCenterInSceneCoordinates();
		idealBounds.x = Math.round(center.x - totalWidth / 2);
		idealBounds.y = Math.round(center.y - totalHeight / 2);
	}

	// Find an empty spot to place the entire group of frames
	const newPosition = findNextFramePosition(idealBounds, direction);

	const targetFrameIds: string[] = [];
	const frameCreationPromises: Promise<void>[] = [];
	for (let i = 0; i < count; i++) {
		const frameId = uuid();
		targetFrameIds.push(frameId);
		frameCreationPromises.push(
			forge.createFrame({
				id: frameId,
				sceneId: activeSceneId,
				title: `${titlePrefix}${i + 1}`,
				x: newPosition.x + i * (device.width + FRAME_GAP),
				y: newPosition.y,
				width: device.width,
				height: device.height,
				metadata: {
					type: frameType,
					status: FrameStatus.PROCESSING,
					statusMessage: 'initializing design generation',
					displayLabels,
					sourceFrameIds,
					sourceChatId: sourceChatId || undefined,
				},
			})
		);
	}

	// Wait for database to create all frames
	await Promise.all(frameCreationPromises);

	const boundingBox: Rect = {
		x: newPosition.x,
		y: newPosition.y,
		width: totalWidth,
		height: device.height,
	};
	store.dispatch(transformCanvasToShowSceneBoundingBox(boundingBox));

	return targetFrameIds;
};

export const isFramePreviewable = (frame: Frame): boolean => {
	switch (frame.metadata.type) {
		case FrameType.MERGE:
		case FrameType.GENERATE:
		case FrameType.GENERATE_FROM_CHAT:
		case FrameType.ENHANCE:
		case FrameType.ADJUST:
		case FrameType.IMPORT_SITE:
			return true;
		case FrameType.IMPORT:
			if (frame.metadata.displayLabels?.['Figma link']) {
				return true;
			}
			return false;
		default:
			return false;
	}
};

//-----------------------------------------------------------------------------
// chat
//-----------------------------------------------------------------------------

export const getChat = (id: string): Chat | null => {
	const state = store.getState();
	const chats = selectChats(state.db);
	return chats.find((chat) => chat.id === id) || null;
};

// does this chat have any generated frames associated with it?
// if so, return true.
// note this will return true even if these frames are not currently placed on the canvas
export const chatHasFrames = (chat: Chat): boolean => {
	for (const entry of chat.entries) {
		if (
			entry.kind === 'system' &&
			(!entry.restriction || entry.restriction === ChatEntryRestriction.USER)
		) {
			const parts = splitMessageWithFrames(entry.message);
			if (parts.some((part) => part.type === 'frames')) {
				return true;
			}
		}
	}
	return false;
};

// given a <frames> XML block from a chat entry, return an array of frame ids
export const parseFramesXml = (xml: string): string[] => {
	const parser = new DOMParser();
	const doc = parser.parseFromString(xml, 'text/xml');
	const frameElements = doc.querySelectorAll('*|frame');
	return Array.from(frameElements).map((frame) => frame.getAttribute('id') || '');
};

export type ChatMessagePart = ChatMessageFramesPart | ChatMessageTextPart;
export type ChatMessageFramesPart = {
	type: 'frames';
	frameIds: string[];
};
export type ChatMessageTextPart = {
	type: 'text';
	content: string;
};

export const splitMessageWithFrames = (message: string): ChatMessagePart[] => {
	const parts: ChatMessagePart[] = [];
	const frameRegex = /\n?<frames>[\s\S]*?<\/frames>\n?/g;
	let lastIndex = 0;
	let match;

	while ((match = frameRegex.exec(message)) !== null) {
		// Add text before the frames block
		if (match.index > lastIndex) {
			const text = message.slice(lastIndex, match.index).trim();
			if (text) {
				parts.push({
					type: 'text',
					content: text,
				});
			}
		}

		// Add the frames block
		parts.push({
			type: 'frames',
			frameIds: parseFramesXml(match[0]),
		});

		lastIndex = match.index + match[0].length;
	}

	// Add any remaining text after the last frames block
	if (lastIndex < message.length) {
		const text = message.slice(lastIndex).trim();
		if (text) {
			parts.push({
				type: 'text',
				content: text,
			});
		}
	}

	return parts;
};

export const getSelectionSvg = async (
	selectionInfo: SelectionInfo,
	siteId: string
): Promise<Blob> => {
	// Send message to the existing iframe to get the selection SVG
	const svg = await new Promise<{ svg: string; bounds: { width: number; height: number } }>(
		(resolve, reject) => {
			const messageHandler = (event: MessageEvent) => {
				if (!isForgeOrigin(event)) return;
				if (event.data?.type !== 'elementSvg') return;
				if (event.data?.siteId !== siteId) return;

				window.removeEventListener('message', messageHandler);

				if (event.data.error) {
					reject(new Error(event.data.error));
					return;
				}

				const svg = event.data.svg as string;
				if (!svg) {
					reject(new Error('No SVG data received'));
					return;
				}
				resolve({
					svg,
					bounds: event.data.bounds,
				});
			};

			window.addEventListener('message', messageHandler);

			// Send the selection info to the iframe
			const iframe = document.querySelector('iframe') as HTMLIFrameElement | null;
			if (!iframe?.contentWindow) {
				window.removeEventListener('message', messageHandler);
				reject(new Error('No iframe found'));
				return;
			}

			// Use typed helper instead of raw postMessage
			sendToDevigner(iframe, siteId, {
				type: 'getSelectionSvg',
				selectionInfo,
			});

			// Set a timeout in case the iframe never responds
			setTimeout(() => {
				window.removeEventListener('message', messageHandler);
				reject(new Error('Timeout waiting for SVG data'));
			}, 10000); // 10 second timeout
		}
	);

	const response = await forge.inlineSvg({
		data: svg.svg,
		width: svg.bounds.width,
		height: svg.bounds.height,
	});
	if (!response.data) throw new Error('Failed to convert SVG to inline');

	return new Blob([response.data], { type: 'image/svg+xml' });
};
